import { Grid, Rhythm } from '@trmediaab/zebra';
import { useSafeLayoutEffect, useWindowEvents } from '@trmediaab/zebra-hooks';
import type {
  Campaign,
  GiftSubscriptionProduct,
  Product,
  SubscriptionProduct,
} from '@trmediaab/zebra-types/trhub';
import { createRef, useCallback, useRef } from 'react';

import type { ProductBoxRefObject } from './SubscriptionProduct';
import ProductBox from './SubscriptionProduct';
import VinternumretProduct from './VinternumretProduct';

interface ProductsListProps {
  products: Product[];
  campaigns: Campaign[];
  onProductSelected: (product: Product) => void;
  onCampaignSelected: (campaign: Campaign) => void;
}

const ProductsList = ({
  products,
  campaigns,
  onProductSelected,
  onCampaignSelected,
}: ProductsListProps) => {
  const gridRef = useRef<HTMLDivElement>(null);
  const gridRefs = useRef<React.RefObject<ProductBoxRefObject | null>[]>([]);

  const [subscriptionProducts, otherProducts] = products.reduce<
    [
      (SubscriptionProduct | GiftSubscriptionProduct)[],
      Exclude<Product, SubscriptionProduct>[],
    ]
  >(
    (acc, product) => {
      if (product.type === 'subscription') {
        acc[0].push(product);
      } else {
        acc[1].push(product);
      }
      return acc;
    },
    [[], []],
  );

  // Custom logic for the "Vinternumret" product
  const vnr = otherProducts.find(product => product.sku === 'MC-TVI');

  if (gridRefs.current.length !== subscriptionProducts.length) {
    gridRefs.current = subscriptionProducts.map(
      (_, idx) =>
        gridRefs.current[idx] || createRef<ProductBoxRefObject | null>(),
    );
  }

  // Note. This won't shrink the elements if window is resized so that the height
  // of the actual content shrinks but that's not too important.
  const setSameHeightStyles = useCallback(() => {
    const [tallestEl1, tallestEl2, tallestEl3, tallestEl4] =
      gridRefs.current.reduce(
        (acc, ref) => {
          if (ref.current) {
            acc[0] = Math.max(ref.current.textEl1?.clientHeight ?? 0, acc[0]);
            acc[1] = Math.max(ref.current.textEl2?.clientHeight ?? 0, acc[1]);
            acc[2] = Math.max(ref.current.textEl3?.clientHeight ?? 0, acc[2]);
            acc[3] = Math.max(ref.current.textEl4?.clientHeight ?? 0, acc[3]);
          }
          return acc;
        },
        [0, 0, 0, 0],
      );

    gridRefs.current.forEach(ref => {
      if (ref.current != null) {
        if (ref.current.textEl1 != null) {
          ref.current.textEl1.style.minHeight = `${tallestEl1}px`;
        }
        if (ref.current.textEl2 != null) {
          ref.current.textEl2.style.minHeight = `${tallestEl2}px`;
        }
        if (ref.current.textEl3 != null) {
          ref.current.textEl3.style.minHeight = `${tallestEl3}px`;
        }
        if (ref.current.textEl4 != null) {
          ref.current.textEl4.style.minHeight = `${tallestEl4}px`;
        }
      }
    });
  }, []);

  useWindowEvents('resize', setSameHeightStyles);

  useSafeLayoutEffect(() => {
    setSameHeightStyles();
  }, [setSameHeightStyles]);

  return (
    <Rhythm between={{ _: 7, md: 8 }}>
      <Grid
        ref={gridRef}
        sx={{
          gap: [4, null, 3, 4],
          gridTemplateColumns: [
            '1fr',
            null,
            '1fr 1fr',
            subscriptionProducts.length === 3
              ? 'repeat(auto-fit, minmax(320px, 1fr))'
              : null,
            subscriptionProducts.length > 4
              ? '1fr 1fr 1fr'
              : subscriptionProducts.length > 3
                ? '1fr 1fr 1fr 1fr'
                : null,
          ],
          justifyContent: 'center',
        }}
      >
        {subscriptionProducts.map((product, idx) => (
          <ProductBox
            key={product._id}
            ref={gridRefs.current[idx]}
            popular={product.popular}
            product={product}
            campaigns={campaigns}
            onProductSelected={onProductSelected}
            onCampaignSelected={onCampaignSelected}
          />
        ))}
      </Grid>
      {vnr != null && (
        <VinternumretProduct
          product={vnr}
          campaigns={campaigns}
          onProductSelected={onProductSelected}
          onCampaignSelected={onCampaignSelected}
        />
      )}
    </Rhythm>
  );
};

export default ProductsList;
